/**
 * Budgets by Category (#2143, epic #2138): the fold behind the Dashboard_N
 * category table, in two layers so the first is reusable anywhere.
 *
 * `summarizeBudgetsByCategory` is the generic fold: plain data in (budgets,
 * categories, per-budget month rows, per-budget balances), plain records out,
 * integer cents, no React, no UI words. Any page, export or report can call
 * it with whatever month rows it holds.
 *
 * `dashboardCategoryTable` is the Dashboard_N shaper on top: the Month to
 * month rows only (#2179), the Default row, the Total, and each row's bar
 * share and bar length (#2207).
 *
 * Signs follow the stores: `spentCents` is negative for money out (as
 * `/budget-months` and `/flows` keep it); balances are signed as the balance
 * cache keeps them, below zero meaning overspent.
 */
import { byName, makeCategoryIdOf } from './budgetGroups';

const BUDGET_TYPE_NORMAL = 0;
export const HORIZON_MONTHLY = 'monthly';
export const HORIZON_PLANNING = 'planningAhead';

/** A budget's horizon (#2141): `planningAhead` when stored, else monthly. */
export const horizonOf = (budget) => (budget && budget.horizon === HORIZON_PLANNING ? HORIZON_PLANNING : HORIZON_MONTHLY);

const sumBy = (rows, field) => rows.reduce((sum, r) => sum + (r[field] || 0), 0);

/**
 * The figures every row and heading carries, over a member list.
 * @param {Array<{name: string, spentCents: number, balanceCents: number}>} members
 */
export const figuresOf = (members) => {
  const over = members.filter((m) => m.balanceCents < 0);
  return {
    budgetCount: members.length,
    spentCents: sumBy(members, 'spentCents'),
    availableCents: sumBy(members, 'balanceCents'),
    overCount: over.length,
    overCents: sumBy(over, 'balanceCents'),
    overNames: over.map((m) => m.name),
  };
};

/**
 * Generic fold: one record per category over its active Normal budgets, plus
 * one record (name null, budgetCategoryId null) for budgets with no category,
 * sorted by name with the uncategorized record last. Members sorted by name.
 * A budget missing from `monthRows` spent nothing; one missing from
 * `byBudget` reads a zero balance.
 *
 * @param {Array} budgets            `/budgets` rows (any types; archived dropped)
 * @param {Array} budgetCategories   `/budgets` budgetCategories
 * @param {Array} monthRows          `/budget-months` budgets for the month
 * @param {Array} byBudget           `/tangible` byBudget rows
 */
export const summarizeBudgetsByCategory = (budgets, budgetCategories, monthRows, byBudget) => {
  const categoryIdOf = makeCategoryIdOf(budgetCategories);
  const categoryName = new Map((budgetCategories || []).map((c) => [c.budgetCategoryId, c.name]));
  const spentOf = new Map((monthRows || []).map((r) => [String(r.budgetId), r.spendingCents || 0]));
  const balanceOf = new Map((byBudget || []).map((r) => [String(r.budgetId), r.balanceCents || 0]));
  const groups = new Map();
  (budgets || [])
    .filter((b) => !b.archived && (b.type ?? BUDGET_TYPE_NORMAL) === BUDGET_TYPE_NORMAL)
    .forEach((b) => {
      const id = categoryIdOf(b);
      const key = id === null ? '' : id;
      if (!groups.has(key)) {
        groups.set(key, { budgetCategoryId: id, name: id === null ? null : categoryName.get(id), members: [] });
      }
      groups.get(key).members.push({
        budgetId: b.budgetId,
        name: b.name,
        horizon: horizonOf(b),
        spentCents: spentOf.get(String(b.budgetId)) || 0,
        balanceCents: balanceOf.get(String(b.budgetId)) || 0,
      });
    });
  return [...groups.values()]
    .map((g) => ({ ...g, members: [...g.members].sort(byName), ...figuresOf(g.members) }))
    .sort((a, b) => {
      if (a.name === null || b.name === null) return (a.name === null) - (b.name === null);
      return byName(a, b);
    });
};

/**
 * Bar share: money spent as a share of spent plus still available. Still
 * available below zero reads as everything spent (1, all red); nothing spent
 * and nothing available means no bar (null).
 */
export const barShareOf = ({ spentCents, availableCents }) => {
  const spent = Math.max(0, -(spentCents || 0));
  if ((availableCents || 0) < 0) return 1;
  const total = spent + (availableCents || 0);
  return total > 0 ? spent / total : null;
};

// The shortest bar (#2207): a small category never vanishes.
const MIN_BAR_LENGTH = 0.1;

/**
 * A category's size for the bar length: Spent, as a positive amount, plus
 * Still available.
 */
const sizeOf = ({ spentCents, availableCents }) => Math.max(0, -(spentCents || 0)) + (availableCents || 0);

/**
 * Bar length (#2207): the bar's width as a share of the name column. The
 * square root of the category's size over the largest category's, so
 * categories of very different sizes stay comparable without the small ones
 * vanishing; never under 10%, and 10% for a size at or below zero.
 * @param {number} sizeCents     this category's Spent + Still available
 * @param {number} largestCents  the largest category's
 * @returns {number} 0.1 .. 1
 */
export const barLengthOf = (sizeCents, largestCents) => {
  if (!(sizeCents > 0) || !(largestCents > 0)) return MIN_BAR_LENGTH;
  return Math.max(MIN_BAR_LENGTH, Math.sqrt(sizeCents / largestCents));
};

/**
 * Dashboard_N shaper (#2179, #2207): one row per category over its Month to
 * month members (horizon `monthly`, or absent), alphabetical with Default
 * last, each with its bar share and bar length; `total` adds those rows up.
 * Planning ahead budgets are left out entirely: they keep their chip in the
 * Budgets groups.
 *
 * @param {Array} summary            summarizeBudgetsByCategory output
 * @param {{default: string}} labels the Default category's display name
 */
export const dashboardCategoryTable = (summary, labels) => {
  const categories = summary
    .map((c) => ({ ...c, members: c.members.filter((m) => m.horizon === HORIZON_MONTHLY) }))
    .filter((c) => c.members.length > 0)
    .map((c) => ({ c, figures: figuresOf(c.members) }));
  const largest = Math.max(0, ...categories.map(({ figures }) => sizeOf(figures)));
  const rows = categories.map(({ c, figures }) => ({
    key: c.budgetCategoryId === null ? 'default' : c.budgetCategoryId,
    kind: 'category',
    name: c.name === null ? labels.default : c.name,
    members: c.members,
    includes: c.members.map((m) => m.name),
    ...figures,
    share: barShareOf(figures),
    length: barLengthOf(sizeOf(figures), largest),
  }));
  return {
    rows,
    total: { key: 'total', kind: 'total', ...figuresOf(rows.flatMap((r) => r.members)) },
  };
};
